import * as React from "react";
import { cn } from "@/lib/utils";
import { CircleInfo } from "@/components/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export interface HintTooltipSection {
  /** 굵은 첫 줄. 없으면 본문만 나온다 */
  title?: string;
  body: React.ReactNode;
}

export interface HintTooltipProps {
  /** 한 툴팁에 여러 항목을 담을 수 있다 (예: 충전소별·충전기별 요금단가) */
  sections: HintTooltipSection[];
  /** ⓘ 아이콘 크기. 라벨 타이포에 맞춘다 */
  iconSize?: number;
  /** ⓘ 아이콘 색 토큰. 파란 안내 배너 안처럼 배경이 있는 자리는 그 배경색을 따른다 */
  iconColor?: string;
  /** 트리거를 직접 넘긴다 (예: 비활성 토글 위 호버). 없으면 ⓘ 버튼 */
  children?: React.ReactNode;
  /** 폭 조정 등 */
  contentClassName?: string;
  /** 본문 타이포·색. 넘기면 기본값을 대체한다 */
  bodyClassName?: string;
}

/**
 * 라벨 옆 ⓘ 호버 안내. 시안의 툴팁은 한 줄 문구가 아니라 **제목 + 본문**이고,
 * 한 툴팁이 두 항목을 담기도 해서(충전기 컬럼) 구조를 컴포넌트로 고정한다.
 *
 * @example
 * ```tsx
 * <HintTooltip
 *   sections={[{ title: "기본 요금단가", body: "별도 단가가 없을 때 적용돼요." }]}
 * />
 * ```
 */
export function HintTooltip({
  sections,
  iconSize = 20,
  iconColor = "labels-assistive",
  children,
  contentClassName,
  bodyClassName = "typo-label-1-medium text-static-white/60",
}: HintTooltipProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {children ?? (
          <button
            type="button"
            aria-label="설명 보기"
            className="flex shrink-0 cursor-help items-center"
          >
            <CircleInfo size={iconSize} color={iconColor} />
          </button>
        )}
      </TooltipTrigger>
      <TooltipContent
        className={cn(
          "flex max-w-70 flex-col gap-3.5 rounded-md p-3 backdrop-blur-[2px]",
          contentClassName,
        )}
      >
        {sections.map((section, index) => (
          <div key={index} className="flex flex-col gap-2">
            {section.title && (
              <span className="typo-label-1-semibold text-static-white whitespace-pre-line">
                {section.title}
              </span>
            )}
            <div className={bodyClassName}>
              {section.body}
            </div>
          </div>
        ))}
      </TooltipContent>
    </Tooltip>
  );
}

HintTooltip.displayName = "HintTooltip";
